import React, { useCallback, useEffect, useRef, useState } from 'react';
import password_on from 'assets/icons/password-on.svg';
import password_off from 'assets/icons/password-off.svg';
import { loginSession, MfaChallenge } from 'config/axiosInstance';
import { clearLogoutUnconfirmed, getRoles, peekLogoutUnconfirmed } from 'config/session';
import { canUseSession } from 'config/sessionEnvironment';
import { homePathFor } from 'router/pathName';
import MfaFlow from './OperatorMfa/MfaFlow';
import { useRequiredFields } from 'reusable/useRequiredFields';
import { FaSpinner } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const LoginForm: React.FC = () => {
  const { t } = useTranslation('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // BE-05（2026-10-14 PM 決定）。「ログイン状態を保持する」。最初は外れている。
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // ADM-23-AC2。直前のログアウトでサーバー側の失効を確認できなかったことを伝える。
  // EV-352。初期化では読むだけにし、描画の後に消す（開発の StrictMode では初期化が2回走る）。
  const [logoutUnconfirmed] = useState(() => peekLogoutUnconfirmed());
  useEffect(() => {
    clearLogoutUnconfirmed();
  }, []);
  // ADM-17（PR #90 レビュー P2）。https か localhost（secure context）で、Web Locks が
  // 使えるブラウザでだけログインさせる。Cookie を書き換える処理をタブ間で直列にできないため。
  const [sessionSupported] = useState(() => canUseSession());
  // EV-436。必須の2欄は required ではなく aria-required で伝え、空のまま送ったときだけここで止める。
  const required = useRequiredFields({ email: 'email', password: 'password' });
  // YAC-2026-009 PR5a。運営の2段階認証のチャレンジ（202）。メモリ（state）だけに置く。
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  // #385 の Codex の指摘 4368093977。2段階認証の流れの世代。パスワードの画面へ戻る・新しく 202 を受け取るたびに
  // 進め、前の流れ（の段）からの呼び戻し（やり直し・ログインの完了）は捨てる。
  const flowRef = useRef(0);
  const [flowId, setFlowId] = useState(0);
  // 運営と分かった（202 を受け取った）メールアドレス。パスワードの画面へ戻ったとき、
  // 「ログイン状態を保持する」が効かないことを、チェックボックスの下に出す。
  const [operatorEmail, setOperatorEmail] = useState<string | null>(null);

  /** 2段階認証の途中からパスワードの画面へ戻す（チャレンジは捨てる。パスワードは入れ直す）。 */
  const restart = useCallback(
    (message: string | null) => {
      flowRef.current += 1;
      setChallenge(null);
      setPassword('');
      setIsLoading(false);
      setErrorMessage(message ? t(message) : '');
    },
    [t]
  );

  /** ログインの後に開く画面（運営だけなら運営の画面）。 */
  const goHome = useCallback(() => {
    window.location.replace(homePathFor(getRoles()));
  }, []);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!sessionSupported) return;
    if (!required.check({ email, password })) return;
    setIsLoading(true);
    setErrorMessage('');

    try {
      // ADM-17。ログイン・役割の確認（RV-040）・Cookie の確認・後始末は loginSession が
      // 1つの錠の中で行う。ここでは結果に応じて移動するか、理由を表示する。
      const outcome = await loginSession(email, password, keepSignedIn);
      if (outcome === 'ok') {
        goHome();
        return;
      }
      if (typeof outcome === 'object' && outcome.kind === 'mfa') {
        // 運営。パスワードは正しく、2段階認証へ進む（トークンはまだ無い）。
        setPassword('');
        setOperatorEmail(email);
        flowRef.current += 1;
        setFlowId(flowRef.current);
        setChallenge(outcome.challenge);
        setIsLoading(false);
        return;
      }
      setErrorMessage(
        outcome === 'not_manager'
          ? t('error_role_message')
          : outcome === 'network'
            ? t('error_network')
            : t('error_cookie_unavailable')
      );
      setIsLoading(false);
    } catch (err: any) {
      setErrorMessage(loginErrorMessage(err));
      setIsLoading(false);
    }
  };

  /** ログインの失敗の理由。パスワード誤りと、権限・接続元・通信の問題を区別する。 */
  const loginErrorMessage = (err: any): string => {
    const code = err?.response?.data?.code;
    if (code === 'role_not_allowed') return t('error_role_message');
    // ADM-17。この画面の Origin がサーバーで許可されていない（CSRF の確認で拒否）。
    if (code === 'csrf_failed') return t('error_origin_not_allowed');
    // BE-20-AC3（2026-10-11 PM 承認の S2）。招待の初期パスワードの期限（7日）切れ。
    if (code === 'invitation_expired') return t('error_invitation_expired');
    // BE-04（2026-10-14 PM 承認の文言）。3回目のロック（60分）からは、管理者に問い合わせる文。
    // 残り時間は出さない。1・2回目のロック（account_locked）は今までどおりパスワードの誤りと同じ文。
    if (code === 'account_locked_contact_admin') return t('error_locked_contact_admin');
    // 2026-10-24 の Safari の確認（作業16）。ログインの回数の制限（429・code throttled、20回/時）。
    // パスワードの誤りの文を出すと、正しい値の人が入力を繰り返して制限が延びるため、待つよう案内する。
    // 残り時間は出さない（Retry-After は別のオリジンの管理画面から読めず、本文にも数の値が無い）。
    if (code === 'throttled' || err?.response?.status === 429) return t('error_throttled');
    // YAC-2026-009 PR3。運営のログインで、確認コードのメールを送れなかった（502）。
    if (code === 'email_not_sent') return t('mfa.error-email-not-sent');
    if (err?.isAxiosError && !err?.response) return t('error_network');
    return t('error_message');
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    required.clear('email');
    setErrorMessage('');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    required.clear('password');
    setErrorMessage('');
  };

  if (challenge) {
    const flow = flowId;
    return (
      <MfaFlow
        key={flow}
        initial={challenge}
        email={email}
        onRestart={(message) => {
          if (flow === flowRef.current) restart(message);
        }}
        onSignedIn={() => {
          if (flow === flowRef.current) goHome();
        }}
      />
    );
  }

  return (
    <form
      onSubmit={handleLogin}
      className="flex flex-col gap-4 md:gap-8 lg:gap-12 w-full mx-20"
    >
      <div className="flex flex-col">
        <span className="text-h5 font-bold">{t('login-form.title')}</span>
        <div>
          <span className="text-h4 font-extra-bold text-primary/300">
            {t('login-form.Rovquest')}
          </span>
          <span className="text-h5 font-bold">{t('login-form.title2')}</span>
        </div>
      </div>
      <div className="flex flex-col gap-6">
        <div className="w-full">
          <label
            htmlFor="email"
            className="block mb-2 text-sm font-regular text-neutral/100"
          >
            {t('login-form.email')}
          </label>
          <input
            type="text"
            id="email"
            className="w-full border border-neutral/50 text-neutral/300 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 pl-4 pr-12"
            placeholder={t('login-form.email-holder')}
            value={email}
            onChange={handleEmailChange}
            {...required.fieldProps('email')}
          />
          {required.message('email')}
        </div>
        <div className="w-full relative">
          <label
            htmlFor="password"
            className="block mb-2 text-sm font-regular text-neutral/100"
          >
            {t('login-form.password')}
          </label>
          <div className="flex items-center relative mb-2">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              className="w-full border border-neutral/50 text-neutral/300 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 pl-4 pr-12"
              placeholder={t('login-form.password-holder')}
              value={password}
              onChange={handlePasswordChange}
              {...required.fieldProps('password')}
            />
            {/* ADM-16。キーボードで押せるボタンにし、操作名と状態（表示中か）を伝える。 */}
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-4"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={t(showPassword ? 'ui.hide-password' : 'ui.show-password', { ns: 'common' })}
              aria-pressed={showPassword}
              aria-controls="password"
            >
              <img src={showPassword ? password_off : password_on} alt="" />
            </button>
          </div>
          {required.message('password', { className: 'mb-2 text-caption text-error' })}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label
              htmlFor="keep-signed-in"
              className="inline-flex items-center gap-2 text-body-2 text-neutral/300 cursor-pointer"
            >
              <input
                type="checkbox"
                id="keep-signed-in"
                className="h-4 w-4 accent-primary/300 cursor-pointer"
                checked={keepSignedIn}
                onChange={(e) => setKeepSignedIn(e.target.checked)}
              />
              {t('login-form.keep-signed-in')}
            </label>
            <a href="forgot-password" className="text-primary/300 ml-auto">
              {t('login-form.forget-password')}
            </a>
          </div>
          {operatorEmail !== null && operatorEmail === email && (
            <p
              data-testid="operator-keep-note"
              className="mt-2 bg-warning/[0.1] text-neutral/300 px-4 py-3 rounded-lg text-body-2"
            >
              {t('mfa.operator-note')}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col">
        {!sessionSupported && (
          <div
            role="alert"
            className="bg-error/[0.1] text-error px-4 py-3 rounded-lg relative mb-4"
          >
            <span className="block sm:inline">{t('error_unsupported_connection')}</span>
          </div>
        )}
        {logoutUnconfirmed && (
          <div
            role="status"
            className="bg-warning/[0.1] text-neutral/300 px-4 py-3 rounded-lg relative mb-4"
          >
            <span className="block sm:inline">{t('logout_unconfirmed')}</span>
          </div>
        )}
        {/* 2026-10-25 axe（color-contrast）。text-error（#de0000）は bg-error/[0.1] の上で 4.5 に届かなかった。
            警告の配色（枠 red-300・地 red-50・文字 red-700。白地で 4.5 以上）にした。 */}
        {errorMessage && (
          <div className="border border-red-300 bg-red-50 text-red-700 px-4 py-3 rounded-lg relative mb-4">
            <span className="block sm:inline">{errorMessage}</span>
          </div>
        )}
        <button
          type="submit"
          className={`flex w-full h-16 rounded-lg justify-center items-center ${
            isLoading ? 'bg-neutral/50 cursor-not-allowed' : 'bg-primary/300'
          } text-title text-white`}
          disabled={isLoading || !sessionSupported}
        >
          {isLoading ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              {t('signing-in')}
            </>
          ) : (
            t('login-form.login')
          )}
        </button>
      </div>
    </form>
  );
};

export default LoginForm;
